import { CartAddEvent } from '@theme/events';

class HrlmBuyCard extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.select = this.querySelector('.hrlm-buy-variant');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('.hrlm-buy-status');
    this.quantity = this.querySelector('[name="quantity"]');
    this.select.addEventListener('change', () => this.updateVariant());
    this.form.addEventListener('submit', (event) => this.addToCart(event));
    if (this.dataset.inlineOptions) this.initInlineOptions();
    this.updateVariant();
  }

  initInlineOptions() {
    this.inlineControls = [...this.querySelectorAll('[data-option-index]')];
    this.optionValues = JSON.parse(this.select.selectedOptions[0].dataset.options);
    this.variants = [...this.select.options];
    for (const control of this.inlineControls) {
      control.addEventListener(control.tagName === 'BUTTON' ? 'click' : 'change', () => {
        if (this.busy) return;
        this.optionValues[Number(control.dataset.optionIndex)] = control.tagName === 'BUTTON' ? control.dataset.optionValue : control.value;
        this.resolveInlineVariant();
      });
    }
    this.syncInlineControls();
  }

  resolveInlineVariant() {
    const variant = this.variants.find(option => JSON.parse(option.dataset.options).every((value, index) => value === this.optionValues[index]));
    if (variant) {
      this.select.value = variant.value;
      this.updateVariant();
    } else {
      this.select.selectedIndex = -1;
      this.button.disabled = true;
      this.querySelector('.hrlm-buy-label').textContent = 'Niet beschikbaar';
      this.status.textContent = 'Deze combinatie is niet beschikbaar. Kies een andere maat of kleur.';
    }
    this.syncInlineControls();
  }

  syncInlineControls() {
    for (const control of this.inlineControls || []) {
      const index = Number(control.dataset.optionIndex);
      control.disabled = Boolean(this.busy);
      if (control.tagName === 'BUTTON') {
        control.setAttribute('aria-pressed', String(control.dataset.optionValue === this.optionValues[index]));
      } else {
        control.value = this.optionValues[index];
        for (const size of control.options) {
          const values = this.optionValues.map((value, position) => position === index ? size.value : value);
          const available = this.variants.some(option => option.dataset.available === 'true' && JSON.parse(option.dataset.options).every((value, position) => value === values[position]));
          // Keep sold-out sizes selectable so their status remains understandable.
          size.textContent = size.value + (available ? '' : ' — Uitverkocht');
        }
      }
    }
  }

  updateVariant() {
    const option = this.select.selectedOptions[0];
    if (!option) return;
    this.button.disabled = this.busy || option.dataset.available !== 'true';
    this.querySelector('.hrlm-buy-label').textContent = option.dataset.available === 'true' ? 'In winkelwagen' : 'Uitverkocht';
    this.querySelector('.hrlm-buy-price').textContent = option.dataset.price;
    const image = this.querySelector('.hrlm-buy-image');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
      image.alt = option.dataset.imageAlt;
    }
    this.quantity.min = option.dataset.min || '1';
    this.quantity.step = option.dataset.step || '1';
    if (option.dataset.max) this.quantity.max = option.dataset.max;
    else this.quantity.removeAttribute('max');
    this.quantity.value = this.quantity.min;
    this.status.textContent = '';
  }

  async addToCart(event) {
    event.preventDefault();
    if (this.busy || this.select.selectedOptions[0]?.dataset.available !== 'true' || !this.form.reportValidity()) return;
    this.busy = true;
    this.button.disabled = true;
    this.select.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    this.syncInlineControls();
    this.status.textContent = 'Wordt toegevoegd…';
    let added = false;
    try {
      // Read the selected ID explicitly: a disabled select is omitted by FormData.
      const body = new FormData(this.form);
      body.set('id', this.select.value);
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean))];
      if (sections.length) body.set('sections', sections.slice(0, 5).join(','));
      body.set('sections_url', window.location.pathname);
      const response = await fetch(this.dataset.addUrl, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Toevoegen is niet gelukt.');
      added = true;
      // A cart read is useful for counters, but must not turn a successful add into an error.
      let cart;
      try {
        const cartResponse = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch { /* Rendered cart sections still contain the successful update. */ }
      this.dispatchEvent(new CartAddEvent(cart, this.id, {
        source: 'hrlm-onepage', productId: this.dataset.productId,
        variantId: this.select.value, sections: result.sections,
        itemCount: Number(body.get('quantity')) || 1,
      }));
      this.status.textContent = 'Toegevoegd aan je winkelwagen.';
    } catch (error) {
      this.status.textContent = added
        ? 'Toegevoegd. Open je winkelwagen om je bestelling te bekijken.'
        : error instanceof TypeError
          ? 'Geen bevestiging ontvangen. Controleer je winkelwagen voordat je opnieuw toevoegt.'
          : error.message || 'Toevoegen is niet gelukt. Probeer het opnieuw.';
    } finally {
      this.busy = false;
      this.select.disabled = false;
      this.button.disabled = this.select.selectedOptions[0]?.dataset.available !== 'true';
      this.form.removeAttribute('aria-busy');
      this.syncInlineControls();
    }
  }
}

if (!customElements.get('hrlm-buy-card')) customElements.define('hrlm-buy-card', HrlmBuyCard);

// Keep product links in the drawer on the storefront and show policies in place.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const policyButton = event.target.closest('[data-hrlm-policy]');
  if (policyButton) {
    const dialog = document.getElementById(policyButton.dataset.hrlmPolicy);
    if (dialog instanceof HTMLDialogElement) dialog.showModal();
  }
  const closeButton = event.target.closest('[data-hrlm-policy-close]');
  if (closeButton) closeButton.closest('dialog')?.close();
  const link = event.target.closest('cart-drawer-component a[href*="#"]');
  if (link) {
    const target = document.getElementById(new URL(link.href).hash.slice(1));
    const drawer = document.querySelector('cart-drawer-component');
    if (target && drawer) {
      event.preventDefault();
      // The mobile drawer consumes its own history entry when it closes.
      if (history.state?.cartDrawerOpen) {
        window.addEventListener('popstate', () => target.scrollIntoView({ block: 'start' }), { once: true });
        drawer.close();
      } else {
        Promise.resolve(drawer.closeDialog()).then(() => target.scrollIntoView({ block: 'start' }));
      }
    } else drawer?.close();
  }
});
